// src/utils/eventBus.ts
import { logError } from './logger';

type EventCallback = (...args: unknown[]) => void;

export const EVENTS = {
  TASK: {
    CREATED: 'TASK_CREATED',
    UPDATED: 'TASK_UPDATED',
    DELETED: 'TASK_DELETED',
    SPLIT: 'TASK_SPLIT',
    STATUS_CHANGED: 'TASK_STATUS_CHANGED',
    BATCH_CREATED: 'TASK_BATCH_CREATED',
    MIGRATION_INCREMENTED: 'TASK_MIGRATION_INCREMENTED'
  },
  TIMEBOX: {
    CREATED: 'TIMEBOX_CREATED',
    UPDATED: 'TIMEBOX_UPDATED',
    DELETED: 'TIMEBOX_DELETED',
    STATUS_CHANGED: 'TIMEBOX_STATUS_CHANGED'
  },
  CAPTURE: {
    CREATED: 'CAPTURE_CREATED',
    UPDATED: 'CAPTURE_UPDATED',
    DELETED: 'CAPTURE_DELETED',
    CONVERTED: 'CAPTURE_CONVERTED'
  },
  GOAL: {
    CREATED: 'GOAL_CREATED',
    UPDATED: 'GOAL_UPDATED',
    DELETED: 'GOAL_DELETED',
    EXTENDED: 'GOAL_EXTENDED',
    CREATE_MODAL_OPENED: 'GOAL_CREATE_MODAL_OPENED',
    CREATE_MODAL_CLOSED: 'GOAL_CREATE_MODAL_CLOSED',
  },
  EVENT: {
    CREATED: 'EVENT_CREATED',
    UPDATED: 'EVENT_UPDATED',
    DELETED: 'EVENT_DELETED',
    BATCH_CREATED: 'EVENT_BATCH_CREATED'
  },
  DAYCLOSE: {
    STARTED: 'DAYCLOSE_STARTED',
    UPDATED: 'DAYCLOSE_UPDATED',
    COMPLETED: 'DAYCLOSE_COMPLETED',
    SKIPPED: 'DAYCLOSE_SKIPPED',
    TIMEBOXES_PROCESSED: 'DAYCLOSE_TIMEBOXES_PROCESSED',
    REFLECTION_SAVED: 'DAYCLOSE_REFLECTION_SAVED',
    TOMORROW_PREVIEWED: 'DAYCLOSE_TOMORROW_PREVIEWED'
  },
  LESSON: {
    // Lesson 引導流程事件
    CAPTURE_TO_TASK_OPENED: 'LESSON_CAPTURE_TO_TASK_OPENED',       // To Task 按鈕被點擊（Modal 開啟）
    CAPTURE_TO_TASK_COMPLETED: 'LESSON_CAPTURE_TO_TASK_COMPLETED', // 任務建立完成
    CAPTURE_INPUT_INTERACTED: 'LESSON_CAPTURE_INPUT_INTERACTED',
    PLAN_APPLIED: 'LESSON_PLAN_APPLIED',                           // Lesson 2：AI 計畫套用完成
    GOAL_TASK_CREATED: 'LESSON_GOAL_TASK_CREATED',                 // Lesson 3：透過 GoalBreakdownPanel 建立第一個 Task
  },
  UI: {
    // Calendar 頁面
    QUICK_CREATE_MODAL_OPENED: 'UI_QUICK_CREATE_MODAL_OPENED',
    AI_PLAN_TRIGGERED: 'UI_AI_PLAN_TRIGGERED',
    TASK_DROPPED_TO_CALENDAR: 'UI_TASK_DROPPED_TO_CALENDAR',
    TASK_SCHEDULED_FROM_LAYER: 'UI_TASK_SCHEDULED_FROM_LAYER',
    // Today 頁面
    AI_SUGGESTION_TRIGGERED: 'UI_AI_SUGGESTION_TRIGGERED',
    TODAY_QUICK_CREATE_OPENED: 'UI_TODAY_QUICK_CREATE_OPENED',
    // Tasks 頁面
    TASK_DETAIL_OPENED: 'UI_TASK_DETAIL_OPENED',
    // Captures 頁面
    INBOX_FILTER_CLICKED: 'UI_INBOX_FILTER_CLICKED',
    // Goals 頁面
    GOAL_ACTIONS_OPENED: 'UI_GOAL_ACTIONS_OPENED',
    GOAL_SUGGEST_CLICKED: 'UI_GOAL_SUGGEST_CLICKED',
    // Event Modal
    ALLDAY_TOGGLED: 'UI_ALLDAY_TOGGLED',
  }
};

class EventBus {
  private events: Record<string, EventCallback[]> = {};

  subscribe(event: string, callback: EventCallback): () => void {
    if (!this.events[event]) {
      this.events[event] = [];
    }

    // 直接添加回調（React useEffect cleanup 機制會確保正確的訂閱/取消訂閱）
    this.events[event].push(callback);

    return () => this.unsubscribe(event, callback);
  }

  unsubscribe(event: string, callback: EventCallback): void {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(cb => cb !== callback);
  }

  publish(event: string, ...args: unknown[]): void {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => {
      try {
        callback(...args);
      } catch (error) {
        logError('EventBus', `Error in event handler for ${event}`, error);
      }
    });
  }

  // 便利方法
  on(event: string, callback: EventCallback): () => void {
    return this.subscribe(event, callback);
  }

  off(event: string, callback: EventCallback): void {
    this.unsubscribe(event, callback);
  }

  emit(event: string, ...args: unknown[]): void {
    this.publish(event, ...args);
  }

  // 用於調試目的
  getSubscriberCount(event: string): number {
    return this.events[event]?.length || 0;
  }
}

export const eventBus = new EventBus();